import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { STICKY_NOTE_COLORS, STICKY_NOTE_MAX_LENGTH } from './stickyNotesStore';
import { StickyNoteIcon } from './StickyNoteIcon';

const EDGE = 16;
const GAP = 6;

/** "daniel.reyes@gigaflightinc.com" -> "Daniel R." */
export const shortAuthor = (value) => {
    const text = String(value || '').trim();
    if (!text) return 'Someone';
    const base = text.includes('@') ? text.split('@')[0] : text;
    const parts = base.split(/[.\s_]+/).filter(Boolean);
    const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
    if (parts.length < 2) return cap(parts[0] || base);
    return `${cap(parts[0])} ${parts[1].charAt(0).toUpperCase()}.`;
};

/** Cetec text often arrives with literal <br> tags. Show them as line breaks. */
const displayText = (value) => String(value || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li)>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/&quot;/gi, '"')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{2,}/g, '\n')
    .trim();

const formatDate = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (!Number.isFinite(d.getTime())) return '';
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const PencilIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
    </svg>
);

const TrashIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 6h18" />
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
        <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
    </svg>
);

/**
 * Popover anchored to a Sticky Notes cell: every note on the order, newest first,
 * with add, edit (text + color) and delete. Closes on outside click or Escape,
 * except while a note is being edited, so a draft is never lost by accident.
 */
export default function StickyNotesBoard({
    anchorRef,
    orderNumber,
    label,
    customerName,
    notes,
    pinned = [],
    shippingComments = [],
    canAddShipping = false,
    onAddShipping,
    onUpdateShipping,
    onRemoveShipping,
    startAdding,
    focusNoteId,
    onAdd,
    onUpdate,
    onRemove,
    onClose,
    apiRef,
    /** Fills the order modal column. Same sections as the cell editor, no popover. */
    embedded = false,
}) {
    const boardRef = useRef(null);
    const textareaRef = useRef(null);
    const [editing, setEditing] = useState(startAdding ? 'new' : null);
    const [text, setText] = useState('');
    const [color, setColor] = useState('yellow');
    const [confirmId, setConfirmId] = useState(null);
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [nudge, setNudge] = useState(false);
    const [highlightId, setHighlightId] = useState(focusNoteId || null);
    const [pos, setPos] = useState({ left: -9999, top: -9999 });

    // `editing` is 'new' (sticky note), 'new-ship' (shipping instruction) or the id
    // of the note being edited - a sticky note or a user-added shipping instruction.
    const isNew = editing === 'new' || editing === 'new-ship';
    const editingNote = editing && !isNew
        ? (notes.find((n) => n.id === editing) || pinned.find((n) => n.id === editing && !n.locked))
        : null;
    const original = isNew ? '' : (editingNote?.text || '');
    const isDirty = Boolean(editing) && (text.trim() !== original.trim() || (editingNote && color !== editingNote.color));

    const doNudge = useCallback(() => {
        setNudge(false);
        requestAnimationFrame(() => setNudge(true));
        textareaRef.current?.focus();
    }, []);

    // The cell asks before another board opens: refuse while there is an unsaved draft.
    if (apiRef) {
        apiRef.current = {
            tryClose: () => {
                if (isDirty) {
                    doNudge();
                    return false;
                }
                onClose?.();
                return true;
            },
        };
    }

    const position = useCallback(() => {
        const anchor = anchorRef.current;
        const board = boardRef.current;
        if (!anchor || !board) return;
        const r = anchor.getBoundingClientRect();
        const bw = board.offsetWidth;
        const bh = board.offsetHeight;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const left = Math.max(EDGE, Math.min(r.left, vw - bw - EDGE));
        let top;
        if (r.bottom + GAP + bh <= vh - EDGE) top = r.bottom + GAP;
        else if (r.top - GAP - bh >= EDGE) top = r.top - GAP - bh;
        else top = Math.max(EDGE, vh - bh - EDGE);
        setPos((prev) => (prev.left === left && prev.top === top ? prev : { left, top }));
    }, [anchorRef]);

    useLayoutEffect(() => {
        if (embedded) return;
        position();
    });

    useEffect(() => {
        if (embedded) return undefined;
        const onMove = () => position();
        window.addEventListener('scroll', onMove, true);
        window.addEventListener('resize', onMove);
        return () => {
            window.removeEventListener('scroll', onMove, true);
            window.removeEventListener('resize', onMove);
        };
    }, [position, embedded]);

    // Outside click closes, unless a draft would be lost.
    useEffect(() => {
        if (embedded) return undefined;
        const onDown = (e) => {
            if (boardRef.current?.contains(e.target)) return;
            if (anchorRef?.current?.contains(e.target)) return;
            apiRef?.current?.tryClose();
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [anchorRef, apiRef, embedded]);

    useEffect(() => {
        if (editing) {
            const ta = textareaRef.current;
            if (ta) {
                ta.focus();
                ta.setSelectionRange(ta.value.length, ta.value.length);
            }
        } else if (!embedded) {
            boardRef.current?.focus();
        }
    }, [editing, embedded]);

    useEffect(() => {
        if (!highlightId) return undefined;
        boardRef.current?.querySelector(`[data-note-id="${CSS.escape(String(highlightId))}"]`)?.scrollIntoView({ block: 'nearest' });
        const timer = setTimeout(() => setHighlightId(null), 1400);
        return () => clearTimeout(timer);
    }, [highlightId]);

    const startNew = () => {
        setEditing('new');
        setText('');
        setColor('yellow');
        setConfirmId(null);
        setError('');
    };

    const startNewShipping = () => {
        setEditing('new-ship');
        setText('');
        setColor('blue');
        setConfirmId(null);
        setError('');
    };

    const startEdit = (note) => {
        setEditing(note.id);
        setText(note.text);
        setColor(note.color);
        setConfirmId(null);
        setError('');
    };

    const cancelEdit = () => {
        setError('');
        if (!embedded && editing === 'new' && !notes.length) {
            onClose?.();
            return;
        }
        setEditing(null);
    };

    const save = async () => {
        const value = text.trim();
        if (!value) {
            setError('Write the instruction before saving.');
            return;
        }
        setBusy(true);
        setError('');
        try {
            if (editing === 'new') {
                const created = await onAdd({ text: value, color });
                setHighlightId(created?.id || null);
            } else if (editing === 'new-ship') {
                const created = await onAddShipping({ text: value, color });
                setHighlightId(created?.id || null);
            } else if (editingNote?.source === 'manual') {
                await onUpdateShipping(editingNote, { text: value, color });
                setHighlightId(editingNote.id);
            } else if (editingNote) {
                await onUpdate(editingNote, { text: value, color });
                setHighlightId(editingNote.id);
            }
            setEditing(null);
        } catch (err) {
            setError(err?.message && !/status code/i.test(err.message)
                ? err.message
                : 'The note could not be saved. Check your connection and try again.');
        } finally {
            setBusy(false);
        }
    };

    const confirmDelete = async (note) => {
        setBusy(true);
        setError('');
        try {
            if (note.source === 'manual') await onRemoveShipping(note);
            else await onRemove(note);
            setConfirmId(null);
        } catch (err) {
            setError('The note could not be deleted. Try again.');
        } finally {
            setBusy(false);
        }
    };

    const onKeyDown = (e) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            if (editing) cancelEdit();
            else if (confirmId) setConfirmId(null);
            else if (!embedded) onClose?.();
        }
    };

    const onEditorKeyDown = (e) => {
        if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            save();
        }
    };

    const renderEditor = (note, shipping = false) => (
        <div className={`sticky-board-note sticky-color-${color} is-editing`} key={note ? note.id : (shipping ? 'new-ship' : 'new')}>
            <div className="sticky-board-paper">
                <label className="sticky-board-label" htmlFor={`sticky-editor-${orderNumber}`}>
                    {shipping
                        ? (note ? 'Edit shipping instruction' : 'New shipping instruction · applies to every order of this customer')
                        : (note ? 'Edit sticky note' : 'New sticky note')}
                </label>
                <textarea
                    id={`sticky-editor-${orderNumber}`}
                    ref={textareaRef}
                    rows={3}
                    maxLength={STICKY_NOTE_MAX_LENGTH}
                    value={text}
                    placeholder={shipping ? "How should this customer's orders be shipped?" : 'What should everyone notice on this order?'}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={onEditorKeyDown}
                    disabled={busy}
                />
                <div className="sticky-board-edit-row">
                    <div className="sticky-board-swatches" role="radiogroup" aria-label="Note color">
                        {STICKY_NOTE_COLORS.map((c) => (
                            <button
                                key={c.id}
                                type="button"
                                role="radio"
                                aria-checked={color === c.id}
                                aria-label={c.label}
                                title={c.label}
                                className={`sticky-board-swatch sticky-color-${c.id}`}
                                onClick={() => setColor(c.id)}
                                disabled={busy}
                            />
                        ))}
                    </div>
                    <span className="sticky-board-count">{text.length} / {STICKY_NOTE_MAX_LENGTH}</span>
                </div>
                {error && <div className="sticky-board-error" role="alert">{error}</div>}
                <div className="sticky-board-actions">
                    <button type="button" className="btn btn-primary btn-sm" onClick={save} disabled={busy}>
                        {busy ? 'Saving…' : 'Save'}
                    </button>
                    <button type="button" className="btn btn-link btn-sm" onClick={cancelEdit} disabled={busy}>
                        Cancel
                    </button>
                    <span className="sticky-board-hint">Ctrl+Enter saves</span>
                </div>
            </div>
        </div>
    );

    const renderNote = (note, index) => {
        if (editing === note.id) return renderEditor(note);
        const confirming = confirmId === note.id;
        return (
            <div
                key={note.id}
                data-note-id={note.id}
                className={`sticky-board-note sticky-color-${note.color}${confirming ? ' is-confirm' : ''}${highlightId === note.id ? ' is-focus' : ''}`}
            >
                <div className="sticky-board-paper">
                    <p className="sticky-board-text">{note.text}</p>
                    <div className="sticky-board-meta">
                        {index === 0 && notes.length > 1 && <span className="sticky-board-latest">Latest</span>}
                        <span>
                            Added by {shortAuthor(note.createdBy)}
                            {note.createdAt ? ` on ${formatDate(note.createdAt)}` : ''}
                            {note.updatedAt && note.updatedAt !== note.createdAt ? ' · Edited' : ''}
                        </span>
                    </div>
                    {confirming ? (
                        <div className="sticky-board-confirm">
                            <span>Delete this sticky note?</span>
                            <button type="button" className="btn btn-danger btn-sm" onClick={() => confirmDelete(note)} disabled={busy} autoFocus>
                                {busy ? 'Deleting…' : 'Delete'}
                            </button>
                            <button type="button" className="btn btn-link btn-sm" onClick={() => setConfirmId(null)} disabled={busy}>
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <div className="sticky-board-tools">
                            <button type="button" className="sticky-board-tool" title="Edit" aria-label="Edit sticky note" onClick={() => startEdit(note)} disabled={Boolean(editing)}>
                                <PencilIcon />
                            </button>
                            <button type="button" className="sticky-board-tool" title="Delete" aria-label="Delete sticky note" onClick={() => { setConfirmId(note.id); setError(''); }} disabled={Boolean(editing)}>
                                <TrashIcon />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderReadonly = (item) => (
        <div
            key={item.id}
            data-note-id={item.id}
            className={`sticky-board-note sticky-color-${item.color} is-pinned${highlightId === item.id ? ' is-focus' : ''}`}
        >
            <div className="sticky-board-paper">
                <p className="sticky-board-text sticky-pinned-text">{displayText(item.text)}</p>
            </div>
        </div>
    );

    /** Cetec instructions are read-only. User-added shipping notes can be edited. */
    const renderPinned = (item) => {
        if (!item.locked && editing === item.id) return renderEditor(item, true);
        const confirming = confirmId === item.id;
        return (
            <div
                key={item.id}
                data-note-id={item.id}
                className={`sticky-board-note sticky-color-${item.color}${item.locked ? ' is-pinned' : ''}${confirming ? ' is-confirm' : ''}${highlightId === item.id ? ' is-focus' : ''}`}
            >
                <div className="sticky-board-paper">
                    <div className="sticky-pinned-label">
                        {item.locked ? (
                            <span>{item.addressType === 'billto' ? 'Bill-to' : item.addressType === 'shipto' ? 'Ship-to' : 'Address'}{item.name ? ` · ${item.name}` : ''} · from Cetec</span>
                        ) : (
                            <span>Added by {shortAuthor(item.createdBy)}{item.createdAt ? ` on ${formatDate(item.createdAt)}` : ''}{item.updatedAt ? ' · Edited' : ''}</span>
                        )}
                    </div>
                    <p className="sticky-board-text sticky-pinned-text">{displayText(item.text)}</p>
                    {!item.locked && (confirming ? (
                        <div className="sticky-board-confirm">
                            <span>Delete this shipping instruction for every order of this customer?</span>
                            <button type="button" className="btn btn-danger btn-sm" onClick={() => confirmDelete(item)} disabled={busy} autoFocus>
                                {busy ? 'Deleting…' : 'Delete'}
                            </button>
                            <button type="button" className="btn btn-link btn-sm" onClick={() => setConfirmId(null)} disabled={busy}>
                                Cancel
                            </button>
                        </div>
                    ) : (
                        <div className="sticky-board-tools">
                            <button type="button" className="sticky-board-tool" title="Edit" aria-label="Edit shipping instruction" onClick={() => startEdit(item)} disabled={Boolean(editing)}>
                                <PencilIcon />
                            </button>
                            <button type="button" className="sticky-board-tool" title="Delete" aria-label="Delete shipping instruction" onClick={() => { setConfirmId(item.id); setError(''); }} disabled={Boolean(editing)}>
                                <TrashIcon />
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    const permanentNotes = pinned.filter((item) => item.locked || item.source === 'cetec');
    const manualShipping = pinned.filter((item) => item.source === 'manual' || (!item.locked && item.source !== 'cetec'));
    const shippingText = (shippingComments || [])
        .map((item) => String(item?.text || '').trim())
        .filter(Boolean)
        .join('\n')
        .replace(/\n{2,}/g, '\n')
        .trim();
    const shippingNote = shippingText
        ? [{
            id: 'shipcomment-combined',
            text: shippingText,
            color: 'periwinkle',
            locked: true,
            source: 'shipping-comment',
        }]
        : [];
    const noteCount = permanentNotes.length + shippingNote.length + manualShipping.length + notes.length;
    const titleId = `sticky-board-title-${orderNumber}`;

    const board = (
        <div
            ref={boardRef}
            className={`sticky-board${embedded ? ' sticky-board-embedded' : ''}${nudge ? ' is-nudge' : ''}`}
            role="dialog"
            aria-labelledby={titleId}
            tabIndex={-1}
            style={embedded ? undefined : { left: pos.left, top: pos.top }}
            onKeyDown={onKeyDown}
            onAnimationEnd={() => setNudge(false)}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <div className="sticky-board-head">
                <div className="sticky-board-titles">
                    <h2 className="sticky-board-title" id={titleId}>
                        <StickyNoteIcon size={16} />
                        Sticky notes
                        {noteCount > 0 && <span className="sticky-board-n">{noteCount}</span>}
                    </h2>
                    <div className="sticky-board-sub">
                        {label || orderNumber}{customerName ? ` · ${customerName}` : ''} · newest first
                    </div>
                </div>
                {!embedded && (
                    <button type="button" className="sticky-board-close" aria-label="Close sticky notes" title="Close" onClick={() => apiRef?.current?.tryClose()}>
                        ×
                    </button>
                )}
            </div>

            <div className="sticky-board-list">
                <div className="sticky-pinned-section">
                    <div className="sticky-pinned-heading">Permanent Instructions</div>
                    {permanentNotes.length
                        ? permanentNotes.map(renderPinned)
                        : <div className="sticky-section-empty">None</div>}
                </div>
                <div className="sticky-pinned-section">
                    <div className="sticky-pinned-heading">Shipping Comments</div>
                    {shippingNote.length
                        ? shippingNote.map(renderReadonly)
                        : <div className="sticky-section-empty">None</div>}
                </div>
                <div className="sticky-pinned-section">
                    <div className="sticky-pinned-heading">Extra Comments</div>
                    {manualShipping.map(renderPinned)}
                    {editing === 'new-ship' && renderEditor(null, true)}
                    {canAddShipping && editing !== 'new-ship' && (
                        <button
                            type="button"
                            className="sticky-board-add sticky-ship-add"
                            onClick={startNewShipping}
                            disabled={Boolean(editing) || busy}
                        >
                            + Add shipping instruction
                        </button>
                    )}
                    {editing === 'new' && renderEditor(null)}
                    {notes.map(renderNote)}
                    {!manualShipping.length && !notes.length && editing !== 'new' && editing !== 'new-ship' && (
                        <div className="sticky-section-empty">None</div>
                    )}
                </div>
                {error && !editing && <div className="sticky-board-error" role="alert">{error}</div>}
            </div>

            {editing !== 'new' && (
                <div className="sticky-board-foot">
                    <button type="button" className="sticky-board-add" onClick={startNew} disabled={Boolean(editing) || busy}>
                        + Add sticky note
                    </button>
                </div>
            )}
        </div>
    );

    if (embedded) return board;
    return createPortal(board, document.body);
}
